/**
 * Order status timeline.
 *
 * The API has no dedicated status endpoint: `GET /customer/orders` IS the
 * polling endpoint and carries per-order and per-item `statusCode` plus an
 * already-translated `statusMessage`. When `statusMessage` is null the bundle
 * string for the code is used — which is why the mapping below exists.
 *
 * "Gọi thêm món" is the same flow as the first order: another
 * `POST /customer/orders` in the same session. There is no separate append
 * endpoint and none is needed.
 */

import { EmptyState, ErrorState, LoadingState } from '@sonauto/ui';
import { useCustomerI18n } from './CustomerI18nProvider';
import type { CustomerOrder, CustomerOrderStatusCode, CustomerOrdersResponse } from '../lib/types';

/** Business code → bundle key + Vietnamese last resort. */
const STATUS_COPY: Record<CustomerOrderStatusCode, { key: string; vi: string; step: number }> = {
  ORDER_RECEIVED: { key: 'customer.order.received', vi: 'Đã nhận đơn', step: 1 },
  ORDER_PREPARING: { key: 'customer.order.preparing', vi: 'Đang chuẩn bị', step: 2 },
  ORDER_COMPLETED: { key: 'customer.order.completed', vi: 'Đã phục vụ', step: 3 },
  ITEM_UNAVAILABLE: { key: 'customer.error.item_unavailable', vi: 'Món không phục vụ được', step: 0 },
};

export interface OrdersScreenProps {
  readonly orders: CustomerOrdersResponse | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly onRetry: () => void;
  readonly onOrderMore: () => void;
  readonly onRate: () => void;
}

export function OrdersScreen({
  orders,
  loading,
  error,
  onRetry,
  onOrderMore,
  onRate,
}: OrdersScreenProps): React.JSX.Element {
  const { tOr, price, locale } = useCustomerI18n();

  if (loading && orders === null) return <LoadingState label={tOr('customer.app.loading', 'Đang tải…')} />;
  if (error !== null && orders === null) {
    return (
      <div className="p-4">
        <ErrorState title={tOr('customer.error.generic', 'Không tải được đơn')} message={error.message} onRetry={onRetry} />
      </div>
    );
  }
  if (orders === null || orders.orders.length === 0) {
    return (
      <div className="px-3 py-6">
        <EmptyState
          title={tOr('customer.order.history_empty', 'Bạn chưa gọi món nào')}
          description={tOr('customer.cart.empty_hint', 'Chọn món trong thực đơn để bắt đầu.')}
        />
        <button
          type="button"
          onClick={onOrderMore}
          className="text-flow mt-4 min-h-[56px] w-full rounded-lg bg-brand-600 px-4 py-3 text-base font-bold text-white"
        >
          {tOr('customer.nav.menu', 'Xem thực đơn')}
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-4">
      <h2 className="text-flow text-lg font-bold">{tOr('customer.order.status_title', 'Đơn của tôi')}</h2>

      <ul className="mt-3 space-y-4">
        {orders.orders.map((order) => (
          <li key={order.orderNo}>
            <OrderCard order={order} price={price} locale={locale} />
          </li>
        ))}
      </ul>

      <div className="mt-6 space-y-2">
        <button
          type="button"
          onClick={onOrderMore}
          className="text-flow min-h-[56px] w-full rounded-lg bg-brand-600 px-4 py-3 text-base font-bold text-white"
        >
          {tOr('customer.order.add_more', 'Gọi thêm món')}
        </button>
        <button
          type="button"
          onClick={onRate}
          className="text-flow min-h-[56px] w-full rounded-lg bg-surface px-4 py-3 text-base font-semibold text-fg ring-1 ring-line"
        >
          {tOr('customer.rate_us', 'Đánh giá phục vụ')}
        </button>
      </div>
    </div>
  );
}

function OrderCard({
  order,
  price,
  locale,
}: {
  readonly order: CustomerOrder;
  readonly price: (amountMinor: number) => string;
  readonly locale: string;
}): React.JSX.Element {
  const { tOr } = useCustomerI18n();
  const copy = STATUS_COPY[order.statusCode];
  const eta =
    order.estimatedReadyAt === null
      ? tOr('customer.order.estimated_time_unknown', 'Đang cập nhật thời gian')
      : tOr('customer.order.estimated_time', 'Dự kiến {minutes} phút', {
          minutes: Math.max(0, Math.round((order.estimatedReadyAt - Date.now()) / 60000)),
        });

  return (
    <article className="rounded-lg bg-surface p-3 shadow-card ring-1 ring-line">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-flow text-base font-bold">
          {tOr('customer.order.number', 'Đơn {number}', { number: order.orderNo })}
        </h3>
        <span className="text-sm text-fg-muted">
          {new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(order.submittedAt)}
        </span>
      </header>

      {/* Timeline. Three steps, no fixed heights, labels wrap. */}
      <ol className="mt-3 flex items-start gap-1">
        {[1, 2, 3].map((step) => {
          const reached = copy.step >= step;
          const label =
            step === 1
              ? tOr('customer.order.received', 'Đã nhận')
              : step === 2
                ? tOr('customer.order.preparing', 'Đang làm')
                : tOr('customer.order.completed', 'Đã phục vụ');
          return (
            <li key={step} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <span
                aria-hidden="true"
                className={`h-2 w-full rounded-full ${reached ? 'bg-success-solid' : 'bg-surface-sunken'}`}
              />
              <span className={`text-flow text-center text-[11px] ${reached ? 'font-semibold text-fg' : 'text-fg-subtle'}`}>
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="text-flow mt-2 text-sm font-semibold text-fg">
        {/* Prefer the server's already-translated message; fall back to the
            bundle string for the stable business code. */}
        {order.statusMessage ?? tOr(copy.key, copy.vi)}
      </p>
      {order.statusCode === 'ORDER_PREPARING' ? <p className="text-flow text-sm text-fg-muted">{eta}</p> : null}

      <ul className="mt-3 divide-y divide-line">
        {order.items.map((item) => {
          const itemCopy = STATUS_COPY[item.statusCode];
          return (
            <li key={item.lineNo} className="flex items-start gap-2 py-2">
              <span className="w-8 shrink-0 text-center font-bold tabular-nums">{item.quantity}</span>
              <span className="text-flow min-w-0 flex-1 text-sm">
                {item.name}
                {item.note !== null ? <span className="block text-xs text-fg-subtle">“{item.note}”</span> : null}
              </span>
              <span className="text-flow shrink-0 text-xs text-fg-muted">
                {item.statusMessage ?? tOr(itemCopy.key, itemCopy.vi)}
              </span>
              <span className="shrink-0 text-sm font-semibold">{price(item.lineTotalMinor)}</span>
            </li>
          );
        })}
      </ul>

      <footer className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
        <span className="text-flow text-sm font-semibold">{tOr('customer.total.grand_total', 'Tổng cộng')}</span>
        <span className="text-lg font-bold">{price(order.totalMinor)}</span>
      </footer>
    </article>
  );
}
